import { Dialog, DialogContent } from '@crono-one/design-system';
import { useConfirmModal } from 'context/confirmModal';
import { Account } from 'crono-fe-common/types/account';
import { Opportunity } from 'crono-fe-common/types/DTO/opportunityViewResponseDTO';
import { Prospect } from 'crono-fe-common/types/prospect';
import useGetEvent from 'hooks/services/event/useGetEvent';
import { useRef } from 'react';
import { MeetingUpsertFallback } from './MeetingUpsertFallback';
import { MeetingUpsertForm } from './MeetingUpsertForm';

interface MeetingUpsertModalProps {
  initialAccount?: Account;
  initialProspect?: Prospect;
  initialOpportunity?: Opportunity;
  eventId?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MeetingUpsertModal({
  initialAccount,
  initialProspect,
  initialOpportunity,
  eventId,
  open,
  onOpenChange,
}: MeetingUpsertModalProps) {
  const { openModal } = useConfirmModal();

  const isDirtyRef = useRef(false);

  // The editor works on the meeting itself rather than a snapshot that can
  // predate the last save. The query stays subscribed while the dialog is
  // closed, so a patch invalidating it refreshes the meeting, and closing
  // unmounts everything below DialogContent — so each open mounts a form that
  // seeds itself from the meeting as it is then, no key needed.
  const {
    data: event,
    isError,
    isSuccess,
  } = useGetEvent(eventId, {
    errorMessage: 'Failed to load the meeting',
  });

  const isEditMode = eventId !== undefined;
  const hasFailed = isError || (isSuccess && !event);

  const close = () => onOpenChange(false);

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }

    if (isDirtyRef.current) {
      openModal({
        title: 'Are you sure you want to close the modal?',
        text: 'You will lose your changes if you close the modal.',
        confirmFunction: close,
      });
      return;
    }

    close();
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent>
        {isEditMode && !event ? (
          <MeetingUpsertFallback hasFailed={hasFailed} onClose={close} />
        ) : (
          <MeetingUpsertForm
            initialAccount={initialAccount}
            initialProspect={initialProspect}
            initialOpportunity={initialOpportunity}
            event={event}
            isDirtyRef={isDirtyRef}
            onClose={close}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
